import React from 'react';
import styled from 'styled-components';

import { colors } from '../../../core';
import Icon from '../Icon';
import Text from '../Text';

export interface CheckboxInputProps {
  checked: boolean;
  className?: string;
  disabled?: boolean;
  id: string;
  label?: string | React.ReactChild;
  name?: string;
  onChange: (e: any) => void;
  errorMessage?: string;
}

const InputWrapper = styled.div``;

const InputContainer = styled.div`
  align-items: center;
  display: flex;
  position: relative;
`;

const Input = styled.input.attrs({
  type: 'checkbox',
})`
  -webkit-appearance: none;
  clip: rect(1px, 1px, 1px, 1px);
  height: 1px;
  margin: 0;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const Label = styled.label`
  color: ${({ theme }) => theme.fontColor || colors.charcoal};
  cursor: pointer;
  font-family: ${({ theme }) => theme.fontFamily || `Quicksand, san-serif`};
  font-size: ${({ theme }) => theme.fontSize || '14px'};
  font-weight: ${({ theme }) => theme.checkboxFontWeight || 600};
  padding-left: 25px;
  padding-top: 1px;
`;

const CheckboxFill = styled(Icon).attrs(({ theme }) => ({
  type: 'checkbox-selected',
  color: theme.checkboxBackgroundActiveColor || colors.primary,
}))`
  position: relative;
`;

const Checkbox = styled.div`
  border-radius: 3px;
  left: 0;
  overflow: hidden;
  position: absolute;
  top: 0;
  height: 20px;
  width: 20px;
  pointer-events: none;

  &::before {
    background-color: ${({ theme }) => theme.checkboxBackgroundColor || colors.white};
    border: 1px solid ${({ theme }) => theme.checkboxBorderColor || colors.smoke};
    border-radius: 3px;
    content: '';
    display: block;
    height: 20px;
    left: 0;
    position: absolute;
    top: 0;
    width: 20px;
  }

  ${Input}:checked + &::before {
    background-color: ${({ theme }) => theme.checkboxBackgroundActiveColor || colors.primary};
    border: 1px solid ${({ theme }) => theme.checkboxBorderActiveColor || colors.primary};
  }

  ${Input}:focus + &::before {
    border: 1px solid ${({ theme }) => theme.checkboxHover || colors.primaryHover};
    box-shadow: 0 0 3px ${({ theme }) => theme.checkboxShadow || colors.primaryHover};
  }

  ${Input}:focus + & {
    box-shadow: 0 0 3px ${({ theme }) => theme.checkboxShadow || colors.primaryHover};
  }

  ${Input}:disabled + & {
    color: ${({ theme }) => theme.checkboxBorderColor || colors.smoke};
    cursor: default;
  }

  ${Input}:disabled:checked + &::before {
    border-color: ${colors.primaryDisabled};
  }
`;

const ErrorMessage = styled(Text)`
  margin: 0;
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.errorColor};
`;

const CheckboxInput = ({
  checked,
  className,
  disabled,
  id,
  label,
  name,
  onChange,
  errorMessage,
}: CheckboxInputProps) => (
  <React.Fragment>
    <InputWrapper>
      <InputContainer className={className}>
        <Input checked={checked} id={id} name={name} onChange={onChange} disabled={disabled} />
        <Checkbox>{checked && <CheckboxFill type="checkbox-selected" />}</Checkbox>
        <Label htmlFor={id}>{label || name}</Label>
      </InputContainer>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </InputWrapper>
  </React.Fragment>
);

export default CheckboxInput;
